import { DataFieldType } from '~/enums/DataFieldType';
import { DataFieldSection } from '~/features/data-fields/types';
import { pilot } from '~/services/serviceInstances';
import { PilotId } from '~/types/PilotId';

import { getPromptModelFieldIds } from '../sylvanus/getPromptModelFieldIds';

interface Request {
  clientId: string;
  options?: {
    includeXRayDetails?: boolean;
  };
}

type DocInfoField = {
  id: PilotId;
  field_type: DataFieldType;
  label: string;
  position: number;
};

type Response = {
  id: PilotId;
  label: string;
  doc_info_fields: DocInfoField[];
  is_default_section: boolean;
  client_id: number;
  position: number;
}[];

export const getClientDataFieldSections = async ({
  clientId,
  options = {
    includeXRayDetails: false,
  },
}: Request): Promise<{
  count: number;
  results: DataFieldSection[];
}> => {
  const response: Response = await pilot.get(`client/${clientId}/sections/`);

  // TODO - move this logic to the BE, this is temporary to unblock g11n release
  const inmutableSectionList = [
    // en
    'General Information',
    'Legal Information',
    'NDA Terms',
    'Financial Information',
    'Operational Information',
    'Nav Triggers',
    'Talent Agreements',
    'Parent Agreement Information',
    'Compliance and Security',

    // de_DE
    'Allgemeine Informationen',
    'Rechtliche Informationen',
    'Vertraulichkeitsbedingungen',
    'Finanzinformationen',
    'Operative Informationen',
    'Navigations-Trigger',
    'Talentvereinbarungen',
    'Informationen zur übergeordneten Vereinbarung',
    'Compliance und Sicherheit',

    // es
    'Información general',
    'Información legal',
    'Términos de NDA',
    'Información financiera',
    'Información operativa',
    'Activadores de navegación',
    'Acuerdos de talento',
    'Información de acuerdo principal',
    'Cumplimiento y seguridad',

    // fr_CA
    'Information générale',
    'Information juridique',
    'Modalités de la FDA',
    'Information financière',
    'Information opérationnelle',
    'Déclencheurs de navigation',
    'Accord sur les talents',
    'Information sur la convention parente',
    'Conformité et sécurité',

    // fr_FR
    'Informations générales',
    'Informations légales',
    'Termes NDA',
    'Informations financières',
    'Informations opérationnelles',
    'Déclencheurs de navigation',
    'Accords sur les talents',
    "Informations sur l'accord parent",
    'Conformité et sécurité',

    // it_IT
    'Informazioni generali',
    'Informazioni legali',
    'Termini NDA',
    'Informazioni finanziarie',
    'Informazioni operative',
    'Trigger navigazione',
    'Accordi talento',
    'Informazioni accordo padre',
    'Conformità e sicurezza',

    // ja_JP
    '概要',
    '法的情報',
    'NDA の条件',
    '財務情報',
    '業務情報',
    'ナビゲーション トリガー',
    'タレント契約',
    '親契約情報',
    'コンプライアンスとセキュリティ',

    // ko_KR
    '일반 정보',
    '법률 정보',
    'NDA 조건',
    '재무 정보',
    '운영 정보',
    '탐색 트리거',
    '인재계약',
    '상위 계약 정보',
    '컴플라이언스 및 보안',

    // nl_NL
    'Algemene gegevens',
    'Juridische informatie',
    'Voorwaarden geheimhoudingsplicht',
    'Financiële gegevens:',
    'Operationele gegevens',
    'Navigatieactiveringen',
    'Talentovereenkomsten',
    'Gegevens bovenliggende overeenkomst',
    'Naleving en beveiliging',

    // pt_BR
    'Informações gerais',
    'Informações legais',
    'Termos de NDA',
    'Informações financeiras',
    'Informações operacionais',
    'Acionamento de navegação',
    'Acordos de talentos',
    'Informações do acordo principal',
    'Conformidade e segurança',

    // zh_CN
    '一般信息',
    '法律信息',
    '保密协议条款',
    '财务信息',
    '业务信息',
    '导航触发器',
    '人才协议',
    '父级协议信息',
    '合规性与安全性',

    // zh_TW
    '一般資訊',
    '法律資訊',
    '保密協議條款',
    '財務資訊',
    '營運資訊',
    '導覽觸發條件',
    '人才協議',
    '上層協議資訊',
    '法規遵循與安全',
  ];

  if (options.includeXRayDetails) {
    const sylvannusModelFieldIds = await getPromptModelFieldIds();

    const newResult = response.map((section: any) => {
      const { doc_info_fields } = section;
      return {
        ...section,
        is_default_section: !!inmutableSectionList.includes(section.label),
        doc_info_fields: doc_info_fields.map((field: DocInfoField) => ({
          ...field,
          is_xray: sylvannusModelFieldIds.includes(field.id),
        })),
      };
    });

    return {
      count: newResult.length,
      results: mapResponse(newResult),
    };
  }

  return {
    count: response.length,
    results: mapResponse(response),
  };
};

const mapResponse = (response: Response) => {
  return response.map((section) => ({
    id: section.id,
    label: section.label,
    docInfoFields: section.doc_info_fields,
    isDefaultSection: section.is_default_section,
    clientId: section.client_id,
    position: section.position,
  }));
};
